import { z } from 'zod';
import type { DonationEvidenceRecipientRole } from './evidence-delivery.js';

/**
 * Per-campaign overrides of the single-page donation entry form's three
 * section headers and its three party labels — GitHub issue #2 §5.2's TODO,
 * decided with the owner 2026-09-25
 * (docs/plans/11-test-revisions.md §11.7,
 * docs/decisions/0081-a-campaigns-entry-labels-override-the-defaults-free-text-on-the-row.md).
 * A recitation campaign renames the on-behalf-of party's label to something
 * closer to "whom this is read for"; nothing in the code branches on a
 * campaign holding one — the fallback is always the i18n default, and every
 * screen that shows one of these six labels asks `resolveEntryLabel` rather
 * than re-implementing the fallback.
 */
export const ENTRY_LABEL_KEYS = [
  'sectionCampaign',
  'sectionParties',
  'sectionNotifications',
  'partyPayer',
  'partyOwner',
  'partyReference',
] as const;

export type EntryLabelKey = (typeof ENTRY_LABEL_KEYS)[number];

/** A key absent here — never sent, or sent blank — means "use the default". */
export type EntryLabelOverrides = Partial<Record<EntryLabelKey, string>>;

/** Bounded like a display name (`campaign.assignment`'s `displayNameSchema`), not like free-form prose. */
export const ENTRY_LABEL_MAX_LENGTH = 120;

const entryLabelShape = Object.fromEntries(
  ENTRY_LABEL_KEYS.map((key) => [key, z.string().max(ENTRY_LABEL_MAX_LENGTH)]),
) as Record<EntryLabelKey, z.ZodString>;

/**
 * What a client may send on `campaign.update`, and what a campaign read hands
 * back: every key optional, and a value bounded but not yet trimmed or
 * emptied — `normaliseEntryLabels` does that before anything is stored, so a
 * blank string sent to clear a key never reaches the database as one.
 */
export const entryLabelOverridesSchema = z.object(entryLabelShape).partial();

/**
 * Trims every value and drops a key whose value is blank, so a stored row
 * never carries a key with nothing to show — sending `''` for a key is how a
 * screen clears an override back to the default, not how it sets one to
 * nothing.
 */
export function normaliseEntryLabels(input: EntryLabelOverrides): EntryLabelOverrides {
  const result: EntryLabelOverrides = {};
  for (const key of ENTRY_LABEL_KEYS) {
    const value = input[key];
    if (value === undefined) continue;
    const trimmed = value.trim();
    if (trimmed.length > 0) result[key] = trimmed;
  }
  return result;
}

/**
 * The one place a screen turns "override or default" into what it shows. A
 * campaign with no override for `key`, or one it never fetched, reads exactly
 * as one whose administrator never opened the settings panel.
 */
export function resolveEntryLabel(
  overrides: EntryLabelOverrides | null | undefined,
  key: EntryLabelKey,
  fallback: string,
): string {
  const value = overrides?.[key];
  return value !== undefined && value.length > 0 ? value : fallback;
}

/**
 * Three of the four notice parties carry their own overridable label; the
 * operator ("entered by") is not one of the three the owner named and always
 * reads as its i18n default.
 */
const ENTRY_LABEL_KEY_BY_PARTY_ROLE: Partial<Record<DonationEvidenceRecipientRole, EntryLabelKey>> =
  {
    payer: 'partyPayer',
    owner: 'partyOwner',
    reference: 'partyReference',
  };

export function entryLabelKeyForPartyRole(
  role: DonationEvidenceRecipientRole,
): EntryLabelKey | null {
  return ENTRY_LABEL_KEY_BY_PARTY_ROLE[role] ?? null;
}
